import React, { useState, useEffect, useRef, useMemo } from "react";
import { Row, Col, Card, CardBody, Form, Label, Button } from "reactstrap";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash.debounce";

import {
  getBranches,
  getStudyLevels,
} from "store/actions";

const SearchSessionResult = ({ onSearch, sessionInfo }) => {
  const dispatch = useDispatch();

  const [selectedBranch,     setSelectedBranch]     = useState(null);
  const [selectedStudyLevel, setSelectedStudyLevel] = useState(null);
  const [selectedSemester,   setSelectedSemester]   = useState(null);

  // 1. Détection robuste du mode Rattrapage
  const isRattrapage = useMemo(() => {
    return sessionInfo?.type === "Rattrapage" || sessionInfo?.is_rattrapage === true;
  }, [sessionInfo]);

  const { branches, studyLevels } = useSelector((state) => ({
    branches:    state.branchReducer.branches        || [],
    studyLevels: state.studyLevelReducer.studyLevels || [],
  }));

  // Style pour forcer l'affichage au-dessus de tout
  // Style pour forcer l'affichage au-dessus (zIndex) et l'opacité (fond blanc)
  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    // On force le fond du menu pour qu'il ne soit plus transparent
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#fff", // Ou la couleur de ton thème
      zIndex: 9999
    }),
    // Optionnel : s'assurer que les options ont aussi un fond correct au survol
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#f8f9fa" : "#fff",
      color: "#333",
    }),
  };

  useEffect(() => {
    dispatch(getBranches());
    dispatch(getStudyLevels());
  }, [dispatch]);

  // Sécurité : Si on bascule en rattrapage, on nettoie le semestre
  useEffect(() => {
    if (isRattrapage) {
      setSelectedSemester(null);
    }
  }, [isRattrapage]);

  // --- Options filtrées ---
  const branchOptions = branches.map((b) => ({
    value: b.id,
    label: b.name,
  }));

  const studyLevelOptions = selectedBranch
    ? studyLevels
        .filter((l) => l.branch_id === selectedBranch.value)
        .map((l) => ({ value: l.id, label: l.name }))
    : [];

  const semesterOptions = selectedStudyLevel
    ? (studyLevels
        .find((l) => l.id === selectedStudyLevel.value)
        ?.semesters || [])
        .map((s) => ({ value: s.id, label: s.code }))
    : [];

  const getCurrentPayload = () => ({
    branch_id:         selectedBranch?.value      || "",
    study_level_id:    selectedStudyLevel?.value  || "",
    study_level_label: selectedStudyLevel?.label  || "",
    semester_id:       selectedSemester?.value    || "",
    semester_label:    selectedSemester?.label    || "",
  });

  // --- Handlers ---
  const handleBranchChange = (selected) => {
    setSelectedBranch(selected);
    setSelectedStudyLevel(null);
    setSelectedSemester(null);
  };

  const handleStudyLevelChange = (selected) => {
    setSelectedStudyLevel(selected);
    setSelectedSemester(null);
  };

  const handleSemesterChange = (selected) => {
    setSelectedSemester(selected);
  };

  // --- Debounce search ---
  const debouncedSearchRef = useRef(
    debounce((payload) => {
      // Logic : En rattrapage, Niveau suffit. Sinon, Semestre obligatoire.
      const canProceed = isRattrapage ? payload.study_level_id : payload.semester_id;
      if (canProceed) {
        onSearch(payload);
      }
    }, 500)
  );

  useEffect(() => {
    const payload = getCurrentPayload();
    const canProceed = isRattrapage ? payload.study_level_id : payload.semester_id;
    if (canProceed) {
        debouncedSearchRef.current(payload);
    }
  }, [selectedBranch, selectedStudyLevel, selectedSemester, isRattrapage]);

  const handleReset = () => {
    setSelectedBranch(null);
    setSelectedStudyLevel(null);
    setSelectedSemester(null);
  };

  const handleSearch = () => {
    debouncedSearchRef.current.cancel(); 
    onSearch(getCurrentPayload());       
  };

  return (
    <Row>
      <Col lg={12}>
        <Card className="session-result-filter">
          <CardBody>
            <Form>
              <Row className="g-3 align-items-end">

                {/* Filière */}
                <Col xxl={isRattrapage ? 4 : 3} lg={isRattrapage ? 5 : 3}>
                  <Label>Filière</Label>
                  <Select
                    options={branchOptions}
                    value={selectedBranch}
                    onChange={handleBranchChange}
                    isClearable
                    placeholder="Sélectionner une filière"
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                </Col>

                {/* Niveau */}
                <Col xxl={isRattrapage ? 4 : 3} lg={isRattrapage ? 5 : 3}>
                  <Label className={!selectedBranch ? "text-muted" : ""}>
                    Niveau (classe)
                  </Label>
                  <Select
                    options={studyLevelOptions}
                    value={selectedStudyLevel}
                    onChange={handleStudyLevelChange}
                    isClearable
                    isDisabled={!selectedBranch}
                    placeholder={
                      selectedBranch ? "Sélectionner un niveau" : "Choisir une filière d'abord"
                    }
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                </Col>

                {/* Semestre : MASQUÉ EN RATTRAPAGE */}
                {!isRattrapage && (
                  <Col xxl={3} lg={3}>
                    <Label className={!selectedStudyLevel ? "text-muted" : ""}>
                      Semestre
                    </Label>
                    <Select
                      options={semesterOptions}
                      value={selectedSemester}
                      onChange={handleSemesterChange}
                      isDisabled={!selectedStudyLevel}
                      isClearable
                      placeholder={
                        selectedStudyLevel ? "Sélectionner un semestre" : "Choisir un niveau d'abord"
                      }
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </Col>
                )}

                {/* Actions */}
                <Col xxl={isRattrapage ? 4 : 3} lg={isRattrapage ? 2 : 3} className="d-flex gap-2">
                  <Button
                    color="primary"
                    type="button"
                    className="w-100"
                    onClick={handleSearch}
                    disabled={isRattrapage ? !selectedStudyLevel : !selectedSemester} 
                  >
                    <i className="bx bx-search-alt me-1"></i> Rechercher
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    type="button"
                    onClick={handleReset}
                  >
                    <i className="bx bx-reset"></i>
                  </Button>
                </Col>

              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default SearchSessionResult;